import React, { useState, useRef, useEffect, useCallback } from "react";

const Tranzicija = ({
  beforeImage,
  afterImage,
  beforeLabel = "PRIJE",
  afterLabel = "POSLIJE",
  width = "100%",
  maxHeight = "80vh",   // ogranič visinu na mobitelu
  beforePosition = "center",
  afterPosition = "center"
}) => {
  const [sliderPosition, setSliderPosition] = useState(50);
  const [isDragging, setIsDragging] = useState(false);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [ratio, setRatio] = useState(null); // width / height
  const containerRef = useRef(null);

  // Uzmi MANJU proporciju od dvije slike (da nijedna nije rezana)
  useEffect(() => {
    let loaded = 0;
    const ratios = [];

    const check = () => {
      loaded++;
      if (loaded === 2) {
        // manja proporcija = viša slika = koristimo nju da obje stanu
        setRatio(Math.min(...ratios));
      }
    };

    [beforeImage, afterImage].forEach((src) => {
      const img = new Image();
      img.onload = () => {
        ratios.push(img.naturalWidth / img.naturalHeight);
        check();
      };
      img.onerror = () => {
        ratios.push(0.75); // fallback
        check();
      };
      img.src = src;
    });
  }, [beforeImage, afterImage]);

  const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

  const updatePosition = useCallback((clientX) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = clientX - rect.left;
    setSliderPosition(clamp((x / rect.width) * 100, 0, 100));
  }, []);

  const handleMouseDown = (e) => {
    e.preventDefault();
    setIsDragging(true);
    setHasInteracted(true);
    updatePosition(e.clientX);
  };

  useEffect(() => {
    const move = (e) => { if (isDragging) updatePosition(e.clientX); };
    const up = () => setIsDragging(false);
    window.addEventListener("mousemove", move);
    window.addEventListener("mouseup", up);
    return () => { window.removeEventListener("mousemove", move); window.removeEventListener("mouseup", up); };
  }, [isDragging, updatePosition]);

  const handleTouchStart = (e) => { setIsDragging(true); setHasInteracted(true); updatePosition(e.touches[0].clientX); };
  const handleTouchMove = (e) => { if (!isDragging) return; e.preventDefault(); updatePosition(e.touches[0].clientX); };
  const handleTouchEnd = () => setIsDragging(false);
  const handleKeyDown = (e) => {
    setHasInteracted(true);
    if (e.key === "ArrowLeft") setSliderPosition((p) => clamp(p - 2, 0, 100));
    else if (e.key === "ArrowRight") setSliderPosition((p) => clamp(p + 2, 0, 100));
  };

  const imgStyle = {
    position: "absolute",
    top: 0, left: 0,
    width: "100%",
    height: "100%",
    objectFit: "cover",
    pointerEvents: "none",
    display: "block"
  };

  return (
    <div style={{ padding: "40px 0" }}>
      <div
        ref={containerRef}
        role="slider"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(sliderPosition)}
        aria-label="Usporedba slike"
        tabIndex={0}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onTouchMove={handleTouchMove}
        onTouchEnd={handleTouchEnd}
        onKeyDown={handleKeyDown}
        style={{
          position: "relative",
          width,
          // aspect-ratio drži proporciju bez rezanja
          aspectRatio: ratio ? `${ratio}` : "4/5",
          maxHeight,
          overflow: "hidden",
          cursor: isDragging ? "grabbing" : "grab",
          userSelect: "none",
          borderRadius: "12px",
          outline: "none",
          boxShadow: isDragging
            ? "0 0 0 2px rgba(255,255,255,0.4)"
            : "0 8px 32px rgba(0,0,0,0.3)"
        }}
      >
        {/* AFTER — pozadina */}
        <img
          src={afterImage}
          alt="Poslije"
          draggable={false}
          style={{ ...imgStyle, objectPosition: afterPosition }}
        />

        {/* BEFORE — isječeno */}
        <img
          src={beforeImage}
          alt="Prije"
          draggable={false}
          style={{
            ...imgStyle,
            objectPosition: beforePosition,
            clipPath: `inset(0 ${100 - sliderPosition}% 0 0)`,
            transition: isDragging ? "none" : "clip-path 0.15s ease",
            zIndex: 2
          }}
        />

        {/* Linija */}
        <div style={{
          position: "absolute", top: 0,
          left: `${sliderPosition}%`,
          transform: "translateX(-50%)",
          height: "100%", width: "2px",
          background: "rgba(255,255,255,0.9)",
          zIndex: 3,
          boxShadow: "0 0 12px rgba(255,255,255,0.5), 0 0 4px rgba(0,0,0,0.3)",
          transition: isDragging ? "none" : "left 0.15s ease",
          pointerEvents: "none"
        }} />

        {/* Handle */}
        <div style={{
          position: "absolute",
          top: "50%", left: `${sliderPosition}%`,
          transform: "translate(-50%, -50%)",
          width: "44px", height: "44px",
          borderRadius: "50%",
          background: "rgba(255,255,255,0.15)",
          border: "2px solid rgba(255,255,255,0.85)",
          backdropFilter: "blur(14px)",
          WebkitBackdropFilter: "blur(14px)",
          zIndex: 5,
          display: "flex", alignItems: "center", justifyContent: "center", gap: "6px",
          transition: isDragging ? "none" : "left 0.15s ease, box-shadow 0.2s ease",
          boxShadow: isDragging
            ? "0 0 0 4px rgba(255,255,255,0.2), 0 4px 16px rgba(0,0,0,0.4)"
            : "0 2px 10px rgba(0,0,0,0.3)",
          animation: hasInteracted ? "none" : "pulse 2s ease-in-out infinite",
          pointerEvents: "none"
        }}>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
            <path d="M7 1L3 5L7 9" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          <svg width="10" height="10" viewBox="0 0 10 10" fill="none" style={{ flexShrink: 0 }}>
            <path d="M3 1L7 5L3 9" stroke="white" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
        </div>

        {/* BEFORE label */}
        <div style={{
          position: "absolute", left: "16px", bottom: "16px",
          padding: "6px 14px", borderRadius: "999px",
          color: "#fff", fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px",
          background: "rgba(0,0,0,0.35)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.18)", zIndex: 6, pointerEvents: "none",
          opacity: sliderPosition > 15 ? 1 : 0, transition: "opacity 0.3s ease"
        }}>{beforeLabel}</div>

        {/* AFTER label */}
        <div style={{
          position: "absolute", right: "16px", bottom: "16px",
          padding: "6px 14px", borderRadius: "999px",
          color: "#fff", fontSize: "11px", fontWeight: 600, letterSpacing: "1.5px",
          background: "rgba(0,0,0,0.35)", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)",
          border: "1px solid rgba(255,255,255,0.18)", zIndex: 6, pointerEvents: "none",
          opacity: sliderPosition < 85 ? 1 : 0, transition: "opacity 0.3s ease"
        }}>{afterLabel}</div>

        <style>{`
          @keyframes pulse {
            0%, 100% { box-shadow: 0 0 0 0 rgba(255,255,255,0.4), 0 2px 10px rgba(0,0,0,0.3); }
            50% { box-shadow: 0 0 0 8px rgba(255,255,255,0), 0 2px 10px rgba(0,0,0,0.3); }
          }
        `}</style>
      </div>
    </div>
  );
};

export default Tranzicija;